// ─── HlsPlayer ────────────────────────────────────────────────────────────────
// Cliente HLS que tira video directo del M4 vía Tailscale.
//
// El operador debe tener Tailscale instalado y conectado al tailnet — sin eso
// el hostname (ej. latadia.copec.retaliq.net) resuelve pero su IP (100.x.y.z)
// no rutea. Es el "prepared workstation" requirement del modelo back-to-back.
//
// Native HLS en Safari (canPlayType 'application/vnd.apple.mpegurl'). En
// Chrome/Firefox usamos hls.js. Latencia típica: 4-6s (segments cortos).
//
// El URL se construye desde un mapa estático site_id → m4_host por ahora.
// Cuando agreguemos más sites lo movemos al remote_config del edge node.

import { useEffect, useRef, useState } from 'react'
import Hls from 'hls.js'

// Cement India demo (cad.retaliq.net) — no hay M4 sidecar; sirve MP4 en loop.
const _isCementDemo = typeof window !== 'undefined' && window.location.hostname === 'cad.retaliq.net'
function _demoVideoForUrl(url) {
  // Extraer channel del path HLS "/live/nvr-01/ch05/index.m3u8"
  const m = /ch(\d+)/i.exec(url || '')
  const channel = m ? parseInt(m[1], 10) : 1
  const idx = (((channel - 1) % 4) + 4) % 4 + 1
  return `/branding/cement-videos/cement-${idx}.mp4`
}

// Config por sitio para acceso back-to-back al M4. Cada EDS nuevo agrega su
// entrada acá. Cuando crezca, se mueve a /api/sites/tree con campos
// `edge_host` y `nvr_label_by_id` en el response.
//
//   host          — hostname del M4 expuesto vía Caddy local + DNS-01
//   nvrLabels     — mapa db_nvr_id → label que usa MediaMTX en el path
//                   (el daemon edge lo genera del remote_config: el primer
//                   NVR es "nvr-01", el segundo "nvr-02", etc. Ese label NO
//                   es igual al db.id que el frontend tiene en mano)
const SITE_CONFIG = {
  17: {
    host: 'latadia.copec.retaliq.net',
    nvrLabels: { 4: 'nvr-01' },
  },
  18: {
    // Lab Ubuntu Cat System — relay MediaMTX expuesto vía Tailscale Funnel
    // (no hay Caddy local; usamos el FQDN público que Tailscale provee).
    host: 'retaliq-edge-lab.tail7ec145.ts.net',
    nvrLabels: { 5: 'nvr-05' },
  },
  19: {
    // Hites Alameda — M4 físico es el mismo de Latadia (rebautizado).
    // Reusa el FQDN latadia.copec.retaliq.net porque el cert + Caddy local
    // del M4 sirven cualquier path. Cuando llegue el M4 nuevo y se separen
    // físicamente las dos cuentas, esto se cambia a hites.copec.retaliq.net.
    // El daemon genera paths MediaMTX con el id raw del NVR (nvr_id=6) en
    // lugar de label cosmético "nvr-01" — por eso el mapeo aquí es { 6: '6' }.
    host: 'latadia.copec.retaliq.net',
    nvrLabels: { 6: '6' },
  },
  // Cat System (M4) site 20 — REMOVIDO 2026-06-09.
  // Paulo elimina el IVSS Dahua y reutiliza el M4 para demo Amersec.
  // Sitio queda en DB con prefijo [INACTIVE 2026-06-09] para historia.
  // Si vuelve: site_id 20, host catsystem.copec.retaliq.net, nvr_id 7 label "7".
}

export function getSiteM4Host(siteId) {
  return SITE_CONFIG[siteId]?.host || null
}

export function buildHlsUrl(siteId, nvrId, channel) {
  const cfg = SITE_CONFIG[siteId]
  if (!cfg || !cfg.host || !nvrId || !channel) return null
  const nvrLabel = cfg.nvrLabels?.[nvrId]
  if (!nvrLabel) return null
  const ch = String(channel).padStart(2, '0')
  return `https://${cfg.host}/live/${nvrLabel}/ch${ch}/index.m3u8`
}

export default function HlsPlayer({
  url,                  // URL HLS del stream — si null, no renderiza nada
  channelName,
  onFallback,           // callback cuando hls.js error fatal — el parent
                        // puede degradar a MJPEG cloud automáticamente
  posterUrl = null,     // (Sprint Performance Live 2026-06-21) snapshot del
                        // NVR como poster mientras HLS hace el primer fetch +
                        // buffer (4-8s). Operador ve "algo" en 200ms.
}) {
  // Cement demo short-circuit: MP4 loop en lugar de HLS al M4.
  if (_isCementDemo) {
    return (
      <div style={{ position: 'relative', width: '100%', height: '100%', background: '#000' }}>
        <video
          src={_demoVideoForUrl(url)}
          autoPlay muted loop playsInline
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
        <div style={{
          position: 'absolute', top: 8, left: 8, display: 'flex', alignItems: 'center', gap: 6,
          padding: '3px 8px', borderRadius: 4, background: 'rgba(0,0,0,0.65)',
          color: '#FFF', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em',
        }}>
          <span style={{
            width: 8, height: 8, borderRadius: '50%', background: '#EF4444',
            display: 'inline-block', animation: 'pulseDot 1.5s ease-in-out infinite',
          }} />
          LIVE
        </div>
        {channelName && (
          <div style={{
            position: 'absolute', bottom: 8, left: 8, padding: '3px 8px',
            borderRadius: 4, background: 'rgba(0,0,0,0.6)', color: '#F1F5F9',
            fontSize: 11, fontWeight: 500,
          }}>
            {channelName}
          </div>
        )}
        <style>{`@keyframes pulseDot { 0%,100%{opacity:1} 50%{opacity:0.35} }`}</style>
      </div>
    )
  }

  const videoRef = useRef(null)
  const [state, setState] = useState('loading')  // loading|playing|error
  const [err,   setErr]   = useState(null)

  useEffect(() => {
    const video = videoRef.current
    if (!video || !url) return
    setState('loading')
    setErr(null)

    let hls = null

    // Preferimos hls.js cuando esté disponible (Chrome/Firefox/Edge): da más
    // control de buffer y reconnect. Native HLS solo si MSE NO está soportado
    // (Safari/iOS sin extensiones). Importante: chequear `Hls.isSupported()`
    // ANTES de canPlayType — Chrome devuelve "maybe" para HLS pero no lo
    // decodifica realmente, y caer al path nativo deja el video sin reproducir.
    if (!Hls.isSupported()) {
      if (video.canPlayType('application/vnd.apple.mpegurl')) {
        // Safari nativo
        video.src = url
        const onPlaying = () => setState('playing')
        const onError = () => { setState('error'); setErr('Native HLS error') }
        video.addEventListener('playing', onPlaying)
        video.addEventListener('error', onError)
        return () => {
          video.removeEventListener('playing', onPlaying)
          video.removeEventListener('error', onError)
          video.removeAttribute('src')
          video.load()
        }
      }
      setState('error')
      setErr('HLS no soportado en este browser')
      return
    }

    hls = new Hls({
      debug: /webrtc_policy=debug/.test(window.location.search),
      lowLatencyMode: false,
      // Streams son SOLO video (sin track de audio). hls.js a veces se queda
      // esperando el bufferCodec event de audio que nunca llega → deadlock.
      // startFragPrefetch fuerza la carga del primer fragment apenas conoce
      // la playlist, sin esperar info de codec, y de ahí extrae el codec real.
      startFragPrefetch: true,
      // Forzar load del primer fragment al inicio del live edge — sin esto
      // hls.js a veces espera N target durations antes de empezar.
      autoStartLoad: true,
      liveSyncDurationCount: 3,
      liveMaxLatencyDurationCount: 10,
      maxBufferLength: 30,
      maxMaxBufferLength: 60,
      // API nueva (v1.5+) — la vieja config era deprecated.
      manifestLoadPolicy: {
        default: {
          maxTimeToFirstByteMs: 20000,
          maxLoadTimeMs: 20000,
          timeoutRetry: { maxNumRetry: 6, retryDelayMs: 800, maxRetryDelayMs: 0 },
          errorRetry:   { maxNumRetry: 6, retryDelayMs: 800, maxRetryDelayMs: 8000 },
        },
      },
      playlistLoadPolicy: {
        default: {
          maxTimeToFirstByteMs: 20000,
          maxLoadTimeMs: 20000,
          timeoutRetry: { maxNumRetry: 6, retryDelayMs: 800, maxRetryDelayMs: 0 },
          errorRetry:   { maxNumRetry: 6, retryDelayMs: 800, maxRetryDelayMs: 8000 },
        },
      },
      fragLoadPolicy: {
        default: {
          maxTimeToFirstByteMs: 20000,
          maxLoadTimeMs: 20000,
          timeoutRetry: { maxNumRetry: 6, retryDelayMs: 800, maxRetryDelayMs: 0 },
          errorRetry:   { maxNumRetry: 6, retryDelayMs: 800, maxRetryDelayMs: 8000 },
        },
      },
    })
    const tagDbg = `[HLS ch${(/ch(\d+)/.exec(url) || [])[1] || '?'}]`
    hls.on(Hls.Events.MANIFEST_LOADED, (e, d) => console.log(tagDbg, 'MANIFEST_LOADED levels=' + d.levels.length))
    hls.on(Hls.Events.MANIFEST_PARSED, (e, d) => console.log(tagDbg, 'MANIFEST_PARSED', d.levels.map(l => l.bitrate)))
    hls.on(Hls.Events.LEVEL_LOADED, (e, d) => console.log(tagDbg, 'LEVEL_LOADED frags=' + d.details.fragments.length, 'live=' + d.details.live, 'targetduration=' + d.details.targetduration))
    hls.on(Hls.Events.FRAG_LOADING, (e, d) => console.log(tagDbg, 'FRAG_LOADING sn=' + d.frag.sn))
    hls.on(Hls.Events.FRAG_LOADED, (e, d) => console.log(tagDbg, 'FRAG_LOADED sn=' + d.frag.sn))
    hls.on(Hls.Events.FRAG_PARSED, (e, d) => console.log(tagDbg, 'FRAG_PARSED sn=' + d.frag.sn))
    hls.on(Hls.Events.BUFFER_APPENDED, (e, d) => console.log(tagDbg, 'BUFFER_APPENDED'))
    hls.loadSource(url)
    hls.attachMedia(video)
    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      video.play().catch(() => {})
    })
    hls.on(Hls.Events.ERROR, (event, data) => {
      if (!data.fatal) return
      switch (data.type) {
        case Hls.ErrorTypes.NETWORK_ERROR:
          // Intentamos recuperar — la red del operador puede parpadear.
          hls.startLoad()
          break
        case Hls.ErrorTypes.MEDIA_ERROR:
          hls.recoverMediaError()
          break
        default:
          setState('error')
          setErr(`${data.type}: ${data.details}`)
          break
      }
    })
    const onPlaying = () => setState('playing')
    video.addEventListener('playing', onPlaying)

    return () => {
      video.removeEventListener('playing', onPlaying)
      try { hls.destroy() } catch {}
    }
  }, [url])

  if (!url) return null

  return (
    <div style={{
      flex: 1, position: 'relative', background: '#000',
      width: '100%', height: '100%',
      overflow: 'hidden', display: 'flex',
      alignItems: 'center', justifyContent: 'center', minHeight: 0,
    }}>
      <video
        ref={videoRef}
        autoPlay playsInline muted
        poster={posterUrl || undefined}
        style={{
          width: '100%', height: '100%',
          objectFit: 'contain', display: 'block',
        }}
      />
      {state === 'loading' && (
        <div style={{
          position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.55)',
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: 8, color: '#e5e5e5',
        }}>
          <div style={{
            width: 22, height: 22, borderRadius: '50%',
            border: '2px solid rgba(255,255,255,0.25)', borderTopColor: '#fff',
            animation: 'spin 0.8s linear infinite',
          }} />
          <span style={{ fontSize: 11 }}>Cargando HD…</span>
        </div>
      )}
      {state === 'error' && (
        <div style={{
          position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.7)',
          display: 'flex', flexDirection: 'column', gap: 8,
          alignItems: 'center', justifyContent: 'center', color: '#e5e5e5',
        }}>
          <span style={{ fontSize: 12 }}>HD direct falló</span>
          <span style={{ fontSize: 10, color: '#aaa', maxWidth: '80%', textAlign: 'center' }}>
            {err}
          </span>
          <span style={{ fontSize: 9, color: '#888', maxWidth: '80%', textAlign: 'center' }}>
            Verificá que tenés Tailscale activo
          </span>
          {onFallback && (
            <button
              onClick={onFallback}
              style={{
                padding: '5px 12px', fontSize: 10, fontWeight: 600,
                background: 'var(--accent)', color: '#fff',
                border: 'none', borderRadius: 4, cursor: 'pointer',
              }}
            >Usar MJPEG cloud (degradado)</button>
          )}
        </div>
      )}
      {channelName && (
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          background: 'linear-gradient(to top, rgba(0,0,0,0.85), transparent)',
          padding: '14px 8px 5px 8px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 6, pointerEvents: 'none',
        }}>
          <span style={{
            fontSize: 11, color: '#e5e5e5', fontWeight: 500,
            textShadow: '0 1px 3px rgba(0,0,0,0.8)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{channelName}</span>
          <span style={{
            fontSize: 9, fontWeight: 700,
            color: state === 'playing' ? '#10b981' : '#f59e0b',
            textShadow: '0 1px 3px rgba(0,0,0,0.8)',
            letterSpacing: '0.05em',
            flexShrink: 0,
          }}>● HLS {state === 'playing' ? 'DIRECT' : state.toUpperCase()}</span>
        </div>
      )}
    </div>
  )
}
